import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@vatman/ui';
import type { TemplateId } from '../../engine/templates';

export function NameDialog({
  open,
  onOpenChange,
  title,
  label,
  initial = '',
  placeholder,
  submitLabel,
  description,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  label: string;
  initial?: string;
  placeholder?: string;
  submitLabel?: string;
  description?: ReactNode;
  onSubmit: (value: string) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setValue(initial);
  }, [open, initial]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try {
      await onSubmit(value.trim());
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} description={description} width={460}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 16, marginTop: description ? 0 : 16 }}>
        <label className="field">
          <span>{label}</span>
          <input className="input" value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} autoFocus maxLength={200} />
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" disabled={busy || !value.trim()}>
            {submitLabel ?? t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  text,
  confirmLabel,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  text: string;
  confirmLabel: string;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} description={text} width={420}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </button>
        <button
          className="btn danger solid"
          onClick={() => {
            onConfirm();
            onOpenChange(false);
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

const box = (left: number, top: number, w: number, h: number, bg: string, extra: React.CSSProperties = {}) => (
  <span style={{ position: 'absolute', left: `${left}%`, top: `${top}%`, width: `${w}%`, height: `${h}%`, background: bg, borderRadius: 3, ...extra }} />
);

/** Tiny abstract preview for each template. */
export function TemplateArt({ id }: { id: TemplateId | 'blank' }) {
  const frame = 'var(--surface)';
  switch (id) {
    case 'blank':
      return (
        <div className="tpl-art" style={{ display: 'grid', placeItems: 'center', fontSize: 28, color: 'var(--text-3)', fontWeight: 300 }}>
          +
        </div>
      );
    case 'brainstorm':
      return (
        <div className="tpl-art">
          {box(40, 38, 20, 24, '#5B4CF0')}
          {box(14, 12, 14, 22, '#FFE066')}
          {box(72, 12, 14, 22, '#B8D4FF')}
          {box(14, 64, 14, 22, '#FFB3BA')}
          {box(72, 64, 14, 22, '#C3EFA6')}
        </div>
      );
    case 'retro':
      return (
        <div className="tpl-art">
          {[0, 1, 2].map((i) => (
            <span key={i}>
              {box(6 + i * 31, 10, 26, 80, frame)}
              {box(10 + i * 31, 18, 9, 16, ['#C3EFA6', '#FFB3BA', '#B8D4FF'][i])}
              {box(21 + i * 31, 18, 9, 16, ['#C3EFA6', '#FFB3BA', '#B8D4FF'][i])}
            </span>
          ))}
        </div>
      );
    case 'kanban':
      return (
        <div className="tpl-art">
          {[0, 1, 2, 3].map((i) => (
            <span key={i}>
              {box(5 + i * 23.5, 10, 20, 80, 'var(--surface-3)')}
              {Array.from({ length: 3 - Math.floor(i / 2) }).map((_, k) => (
                <span key={k}>
                  {box(7.5 + i * 23.5, 17 + k * 22, 15, 17, frame, {
                    borderLeft: `3px solid ${['#6B7280', '#F5A524', '#0090FF', '#30A46C'][i]}`,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)',
                  })}
                </span>
              ))}
            </span>
          ))}
        </div>
      );
    case 'mindmap':
      return (
        <div className="tpl-art">
          {box(38, 40, 24, 18, '#5B4CF0', { borderRadius: 6 })}
          {[0, 1, 2].map((k) => (
            <span key={k}>
              {box(8, 14 + k * 26, 18, 13, ['#FFE066', '#B8D4FF', '#C3EFA6'][k], { borderRadius: 5 })}
              {box(74, 14 + k * 26, 18, 13, ['#FFB3BA', '#D9C5FF', '#FFC98B'][k], { borderRadius: 5 })}
            </span>
          ))}
        </div>
      );
    case 'swot':
      return (
        <div className="tpl-art">
          {box(8, 8, 40, 40, '#E9F8EF')}
          {box(52, 8, 40, 40, '#FDEBEC')}
          {box(8, 52, 40, 40, '#E8F1FD')}
          {box(52, 52, 40, 40, '#FFF6E5')}
        </div>
      );
    case 'journey':
      return (
        <div className="tpl-art">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i}>
              {box(8 + i * 18, 10, 15, 12, '#5B4CF0')}
              {box(10 + i * 18, 30, 11, 14, '#FFE066')}
              {box(10 + i * 18, 50, 11, 14, '#F7B5E4')}
              {box(10 + i * 18, 70, 11, 14, '#C3EFA6')}
            </span>
          ))}
        </div>
      );
  }
}
